import { useQuery } from "@tanstack/react-query";
import { meta, operators as operatorsApi, records } from "../../api/endpoints";
import type { RecordFilter } from "../../api/types";
import { Button, Input, Label, Select } from "../../components/ui";
import { DATE_PRESETS, endOfDayIso, presetToRange, startOfDayIso, todayInputValue, type DatePreset } from "../../lib/dates";

/**
 * Everything the desktop FilterBar offers, as a collapsible panel.
 *
 * Filters are held as this draft shape rather than as a RecordFilter so the
 * form can hold half-typed input (an empty min box, a date range being picked)
 * without that reaching the query. `toCriteria` is the one place a draft
 * becomes something the API will accept.
 */
export interface FilterDraft {
  preset: DatePreset;
  dateFrom: string;
  dateTo: string;
  platform: string;
  reason: string;
  command: string;
  symbol: string;
  operatorId: string;
  status: string;
  clientId: string;
  accountNumber: string;
  compMin: string;
  compMax: string;
  profitMin: string;
  profitMax: string;
  lotMin: string;
  lotMax: string;
}

export const EMPTY_FILTER: FilterDraft = {
  preset: "All Time",
  dateFrom: todayInputValue(),
  dateTo: todayInputValue(),
  platform: "",
  reason: "",
  command: "",
  symbol: "",
  operatorId: "",
  status: "All Statuses",
  clientId: "",
  accountNumber: "",
  compMin: "",
  compMax: "",
  profitMin: "",
  profitMax: "",
  lotMin: "",
  lotMax: "",
};

/** (label, is_done, is_rejected) — mirrors filter_bar.STATUS_OPTIONS. Done and
 * Rejected are independent columns, so most options constrain exactly one.
 * "Pending" is the exception — it constrains both at once, to the
 * not-yet-actioned state on both ticks. */
const STATUS_OPTIONS: [string, boolean | null, boolean | null][] = [
  ["All Statuses", null, null],
  ["Pending", false, false],
  ["Done", true, null],
  ["Not Done", false, null],
  ["Rejected", null, true],
  ["Not Rejected", null, false],
];

const money = (text: string): string | null => {
  const trimmed = text.trim();
  return trimmed === "" ? null : trimmed;
};

export function toCriteria(draft: FilterDraft, search: string): RecordFilter {
  const status = STATUS_OPTIONS.find((option) => option[0] === draft.status) ?? STATUS_OPTIONS[0]!;

  let dateFrom: string | null;
  let dateTo: string | null;
  if (draft.preset === "Custom Range") {
    dateFrom = startOfDayIso(draft.dateFrom);
    dateTo = endOfDayIso(draft.dateTo);
  } else {
    const range = presetToRange(draft.preset);
    dateFrom = range.from;
    dateTo = range.to;
  }

  return {
    search: search.trim() || null,
    date_from: dateFrom,
    date_to: dateTo,
    client_id: draft.clientId.trim() || null,
    account_number: draft.accountNumber.trim() || null,
    platforms: draft.platform ? [draft.platform] : [],
    reasons: draft.reason ? [draft.reason] : [],
    commands: draft.command ? [draft.command] : [],
    symbols: draft.symbol ? [draft.symbol] : [],
    operator_ids: draft.operatorId ? [Number(draft.operatorId)] : [],
    compensation_min: money(draft.compMin),
    compensation_max: money(draft.compMax),
    profit_min: money(draft.profitMin),
    profit_max: money(draft.profitMax),
    lot_min: money(draft.lotMin),
    lot_max: money(draft.lotMax),
    is_done: status[1],
    is_rejected: status[2],
  };
}

/** Whether anything beyond the quick-search box is constraining the query —
 * drives the "N active" hint on the Filters button. */
export function activeFilterCount(draft: FilterDraft): number {
  let count = 0;
  if (draft.preset !== "All Time") count += 1;
  for (const key of [
    "platform",
    "reason",
    "command",
    "symbol",
    "operatorId",
    "clientId",
    "accountNumber",
    "compMin",
    "compMax",
    "profitMin",
    "profitMax",
    "lotMin",
    "lotMax",
  ] as const) {
    if (draft[key].trim() !== "") count += 1;
  }
  if (draft.status !== "All Statuses") count += 1;
  return count;
}

export function FilterBar({
  draft,
  onChange,
  onClear,
}: {
  draft: FilterDraft;
  onChange: (next: FilterDraft) => void;
  onClear: () => void;
}) {
  const metaQuery = useQuery({ queryKey: ["meta"], queryFn: () => meta.get(), staleTime: 300_000 });
  const symbolsQuery = useQuery({
    queryKey: ["records", "distinct", "symbol"],
    queryFn: () => records.distinct("symbol"),
    staleTime: 60_000,
  });
  const operatorsQuery = useQuery({
    queryKey: ["operators", "all"],
    queryFn: () => operatorsApi.list(),
    staleTime: 60_000,
  });

  const set = <K extends keyof FilterDraft>(key: K, value: FilterDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const customRange = draft.preset === "Custom Range";

  return (
    <div className="card mb-3 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Label>Date</Label>
          <Select value={draft.preset} onChange={(event) => set("preset", event.target.value as DatePreset)}>
            {DATE_PRESETS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>From / To</Label>
          <div className="flex gap-2">
            <Input
              type="date"
              value={draft.dateFrom}
              max={draft.dateTo}
              disabled={!customRange}
              onChange={(event) => set("dateFrom", event.target.value)}
            />
            <Input
              type="date"
              value={draft.dateTo}
              min={draft.dateFrom}
              max={todayInputValue()}
              disabled={!customRange}
              onChange={(event) => set("dateTo", event.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Platform</Label>
          <Select value={draft.platform} onChange={(event) => set("platform", event.target.value)}>
            <option value="">All Platforms</option>
            {metaQuery.data?.records.platforms.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Reason</Label>
          <Select value={draft.reason} onChange={(event) => set("reason", event.target.value)}>
            <option value="">All Reasons</option>
            {metaQuery.data?.records.reasons.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Symbol</Label>
          <Select value={draft.symbol} onChange={(event) => set("symbol", event.target.value)}>
            <option value="">All Symbols</option>
            {symbolsQuery.data?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Operator</Label>
          <Select
            value={draft.operatorId}
            onChange={(event) => set("operatorId", event.target.value)}
          >
            <option value="">All Operators</option>
            {operatorsQuery.data?.map((operator) => (
              <option key={operator.id} value={String(operator.id)}>
                {operator.name}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Command</Label>
          <Select value={draft.command} onChange={(event) => set("command", event.target.value)}>
            <option value="">All Commands</option>
            {metaQuery.data?.records.commands.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Status</Label>
          <Select value={draft.status} onChange={(event) => set("status", event.target.value)}>
            {STATUS_OPTIONS.map(([label]) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Client / Account</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Client ID"
              value={draft.clientId}
              onChange={(event) => set("clientId", event.target.value)}
            />
            <Input
              placeholder="Account"
              value={draft.accountNumber}
              onChange={(event) => set("accountNumber", event.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Compensation (USD)</Label>
          <div className="flex gap-2">
            <Input
              inputMode="decimal"
              placeholder="Min"
              value={draft.compMin}
              onChange={(event) => set("compMin", event.target.value)}
            />
            <Input
              inputMode="decimal"
              placeholder="Max"
              value={draft.compMax}
              onChange={(event) => set("compMax", event.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Profit (USD)</Label>
          <div className="flex gap-2">
            <Input
              inputMode="decimal"
              placeholder="Min"
              value={draft.profitMin}
              onChange={(event) => set("profitMin", event.target.value)}
            />
            <Input
              inputMode="decimal"
              placeholder="Max"
              value={draft.profitMax}
              onChange={(event) => set("profitMax", event.target.value)}
            />
          </div>
        </div>

        <div>
          <Label>Lot</Label>
          <div className="flex gap-2">
            <Input
              inputMode="decimal"
              placeholder="Min"
              value={draft.lotMin}
              onChange={(event) => set("lotMin", event.target.value)}
            />
            <Input
              inputMode="decimal"
              placeholder="Max"
              value={draft.lotMax}
              onChange={(event) => set("lotMax", event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <Button variant="ghost" onClick={onClear}>
          Clear Filters
        </Button>
      </div>
    </div>
  );
}
